import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiPlus, FiSave, FiTrash2, FiUsers } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { useAdminAccess } from '../utilities/adminAccess';
import { champFields, deriveGroupsFromServer, extractEntity, extractList, isEnabled } from '../utilities/tournamentsApi';
import { formatKickoff, mergeLeagueInfoCache, normalizeAdminMatch } from '../utilities/matchesApi';
import CustomModal from '../components/CustomModal';
import StadiumCrumbs from '../components/StadiumCrumbs';
import CreateMatchModal from '../components/CreateMatchModal';
import GenerateTourModal from '../components/GenerateTourModal';
import '../styles/createMatchModal.css';
import '../styles/stadiumsManagement.css';
import '../styles/disqualificationsManagement.css';
import '../styles/tournamentsManagement.css';
import '../styles/stagesManagement.css';

const RATE_RATIOS = [20, 30, 40, 50, 60];

const emptyForm = {
  stage_name: '',
  short_stage_name: '',
  isPlayoff: false,
  rate_ratio: '',
  group_id: '',
};

const parseMatches = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.matches)) return data.matches;
  if (Array.isArray(data?.data?.matches)) return data.data.matches;
  return [];
};

const matchBelongsToStage = (match, stageId) => {
  const ids = [match?.stage_id, match?.tour_id, match?.stageId, match?.tourId]
    .map(Number)
    .filter((value) => Number.isFinite(value) && value > 0);
  if (!ids.length) return true;
  return ids.includes(Number(stageId));
};

const formatMatchWhen = (match) => formatKickoff(match);

export default function ManageTournamentStage() {
  const { countryId, leagueId, seasonId, stageId } = useParams();
  const isNew = !stageId;
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useI18n();
  const { champId, selected: championship, loading: champsLoading } = useChampionships();
  const { canWrite } = useAdminAccess('tournaments');
  const leaguesAccess = useAdminAccess('leagues');

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [leagueName, setLeagueName] = useState('');
  const [countryName, setCountryName] = useState('');
  const [seasonName, setSeasonName] = useState('');
  const [isGroupTournament, setIsGroupTournament] = useState(false);
  const [seasonGroups, setSeasonGroups] = useState([]);
  const [leagueRef, setLeagueRef] = useState(null);
  const [seasonRef, setSeasonRef] = useState(null);
  const [previousStage, setPreviousStage] = useState(null);
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [createMatchOpen, setCreateMatchOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [matchToDelete, setMatchToDelete] = useState(null);
  const [deletingMatch, setDeletingMatch] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    if (!champId && champsLoading) return;
    initPage();
  }, [countryId, leagueId, seasonId, stageId, champId, champsLoading]);

  const goBackToSeason = () => {
    history.push(`/tournaments/${countryId}/league/${leagueId}/season/${seasonId}`);
  };

  const buildVenueContext = useCallback((league = leagueRef, cityLabel = countryName) => ({
    ...(league || {}),
    venue_timezone: league?.venue_timezone
      || league?.timezone
      || championship?.venue_timezone
      || championship?.timezone,
    timezone: league?.venue_timezone
      || league?.timezone
      || championship?.venue_timezone
      || championship?.timezone,
    city_name: cityLabel || league?.city_name || championship?.city || championship?.city_ru || '',
    city: cityLabel || championship?.city || championship?.city_ru || '',
    championship,
    league,
  }), [leagueRef, championship, countryName]);

  const loadMatches = useCallback(async (venueOverride = null) => {
    if (isNew || !leagueId || !seasonId || !stageId) return;
    setMatchesLoading(true);
    try {
      const response = await authorizedPost(dispatch, `/leagues/${leagueId}/matches`, {
        ...champFields(champId),
        season_id: Number(seasonId),
        stage_id: Number(stageId),
      });
      const venueCtx = venueOverride || buildVenueContext();
      const list = parseMatches(response.data)
        .map((match) => normalizeAdminMatch(match, venueCtx))
        .filter((match) => match.id && matchBelongsToStage(match, stageId));
      setMatches(list);
    } catch (error) {
      setMatches([]);
      setMessage({ text: getApiErrorMessage(error, t('stLoadError')), type: 'error' });
    } finally {
      setMatchesLoading(false);
    }
  }, [dispatch, isNew, leagueId, seasonId, stageId, champId, t, buildVenueContext]);

  const handleMatchesChanged = () => {
    setCreateMatchOpen(false);
    setGenerateOpen(false);
    loadMatches();
    window.setTimeout(() => {
      loadMatches();
    }, 800);
  };

  const initPage = async () => {
    setMessage({ text: '', type: '' });
    setErrors({});
    setLoading(true);
    try {
      const requests = [
        authorizedPost(dispatch, API_URLS.getAdminLeague, { id: Number(leagueId) }),
        authorizedPost(dispatch, API_URLS.getAdminSeason, { id: Number(seasonId) }),
      ];
      if (champId) {
        requests.push(authorizedPost(dispatch, API_URLS.getAdminCountries, { champ_id: champId }));
      }

      const [leagueResponse, seasonResponse, countriesResponse] = await Promise.all(requests);
      const league = mergeLeagueInfoCache(extractEntity(leagueResponse.data) || {});
      const season = extractEntity(seasonResponse.data);
      const settings = season.league_settings || {};
      const groupTournament = Number(settings.league_type) === 2;

      setLeagueName(league.name || '');
      setSeasonName(season.name || '');
      setIsGroupTournament(groupTournament);

      if (groupTournament) {
        try {
          const groupsResponse = await authorizedPost(dispatch, API_URLS.getAdminSeasonGroups, {
            ...champFields(champId),
            league_id: Number(leagueId),
            season_id: Number(seasonId),
          });
          const derived = deriveGroupsFromServer(groupsResponse.data);
          setSeasonGroups(derived.list
            .map((group) => ({
              id: Number(group.serverId ?? group.id),
              name: group.name,
            }))
            .filter((group) => Number.isFinite(group.id) && group.id > 0));
        } catch (error) {
          setSeasonGroups([]);
        }
      } else {
        setSeasonGroups([]);
      }

      let nextCountryName = '';
      if (countriesResponse) {
        const countries = extractList(countriesResponse.data);
        const country = countries.find((item) => Number(item.id) === Number(countryId));
        nextCountryName = country?.name || '';
        setCountryName(nextCountryName);
      }

      const nextLeagueRef = {
        ...league,
        value: Number(leagueId),
        id: Number(leagueId),
        name: league.name || '',
        country_id: Number(countryId) || league.country_id,
        country_name: nextCountryName || league.country_name || '',
        league_type: Number(settings.league_type ?? league.league_type ?? 1),
      };
      setLeagueRef(nextLeagueRef);
      setSeasonRef({
        ...season,
        value: Number(seasonId),
        id: Number(seasonId),
        name: season.name || '',
      });

      if (isNew) {
        setForm(emptyForm);
        setPreviousStage(null);
        setMatches([]);
        return;
      }

      const stages = extractList(season.stages);
      const stage = stages.find((item) => Number(item.id) === Number(stageId));
      if (!stage) {
        setMessage({ text: t('trStageNotFound'), type: 'error' });
        setForm(emptyForm);
        setPreviousStage(null);
        return;
      }

      const previous = stages
        .filter((item) => Number(item.id) < Number(stageId))
        .sort((a, b) => Number(b.id) - Number(a.id))[0] || null;
      setPreviousStage(previous ? {
        id: Number(previous.id),
        name: previous.stage_name || previous.name || '',
        short_name: previous.short_stage_name || previous.short_name || '',
      } : null);

      setForm({
        stage_name: stage.stage_name || '',
        short_stage_name: stage.short_stage_name || '',
        isPlayoff: isEnabled(stage.isPlayoff),
        rate_ratio: stage.rate_ratio == null ? '' : String(stage.rate_ratio),
        group_id: stage.group_id ? String(stage.group_id) : '',
      });

      await loadMatches(buildVenueContext(nextLeagueRef, nextCountryName));
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const handleSave = async () => {
    if (!form.stage_name.trim()) {
      setErrors({ stage_name: t('trStageNameRequired') });
      return;
    }

    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const payload = {
        league_id: Number(leagueId),
        season_id: Number(seasonId),
        stage_name: form.stage_name.trim(),
        short_stage_name: form.short_stage_name.trim() || form.stage_name.trim(),
      };

      if (isGroupTournament) {
        payload.isPlayoff = form.isPlayoff ? 1 : 0;
        payload.group_id = !form.isPlayoff && form.group_id ? Number(form.group_id) : null;
        if (isNew) {
          if (form.rate_ratio !== '') {
            payload.rate_ratio = Number(form.rate_ratio);
          }
        } else {
          payload.rate_ratio = form.rate_ratio === '' ? null : Number(form.rate_ratio);
        }
      }

      if (isNew) {
        await authorizedPost(dispatch, API_URLS.addStage, payload);
        setMessage({ text: t('trStageCreated'), type: 'success' });
        goBackToSeason();
      } else {
        await authorizedPost(dispatch, API_URLS.changeStage, {
          ...payload,
          id: Number(stageId),
        });
        setMessage({ text: t('stSaved'), type: 'success' });
      }
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stSaveError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await authorizedPost(dispatch, API_URLS.deleteStage, { id: Number(stageId) });
      goBackToSeason();
    } catch (error) {
      setDeleteVisible(false);
      setMessage({ text: getApiErrorMessage(error, t('stdDeleteError')), type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteMatch = async () => {
    const matchId = Number(matchToDelete?.id ?? matchToDelete?.match_id);
    if (!matchId) return;
    setDeletingMatch(true);
    setMessage({ text: '', type: '' });
    try {
      await authorizedPost(dispatch, API_URLS.deleteMatch, { match_id: matchId });
      setMatches((prev) => prev.filter((item) => Number(item.id ?? item.match_id) !== matchId));
      setMatchToDelete(null);
      setMessage({ text: t('trMatchDeleted'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stdDeleteError')), type: 'error' });
    } finally {
      setDeletingMatch(false);
    }
  };

  const stageForModals = !isNew ? {
    id: Number(stageId),
    name: form.stage_name.trim(),
    short_name: form.short_stage_name.trim(),
    isPlayoff: form.isPlayoff ? 1 : 0,
    group_id: !form.isPlayoff && form.group_id ? Number(form.group_id) : null,
  } : null;

  const crumbs = [
    { label: t('navTournaments'), to: '/tournaments' },
    { label: countryName || t('trCountry'), to: `/tournaments/${countryId}` },
    { label: leagueName || t('trEditLeague'), to: `/tournaments/${countryId}/league/${leagueId}` },
    { label: seasonName || t('trEditSeason'), to: `/tournaments/${countryId}/league/${leagueId}/season/${seasonId}` },
    { label: isNew ? t('trNewStage') : (form.stage_name.trim() || t('trEditStage')) },
  ];

  const stageForm = (
    <div className="st-form-grid">
      <div className="st-field">
        <label>{t('trStageName')} <span className="st-required">*</span></label>
        <input
          className={`st-input ${errors.stage_name ? 'error' : ''}`}
          value={form.stage_name}
          placeholder={t('trStageNamePh')}
          onChange={(event) => updateField('stage_name', event.target.value)}
        />
        {errors.stage_name ? <span className="st-field-error">{errors.stage_name}</span> : null}
      </div>

      <div className="st-field">
        <label>{t('trStageShortName')}</label>
        <input
          className="st-input"
          value={form.short_stage_name}
          placeholder={t('trStageShortNamePh')}
          onChange={(event) => updateField('short_stage_name', event.target.value)}
        />
      </div>

      {isGroupTournament ? (
        <>
          <div className="st-field st-field-wide">
            <label className="st-check">
              <input
                type="checkbox"
                checked={form.isPlayoff}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setForm((prev) => ({
                    ...prev,
                    isPlayoff: checked,
                    group_id: checked ? '' : prev.group_id,
                  }));
                }}
              />
              {t('stPlayoff')}
            </label>
            <span className="st-photo-hint">{t('trStagePlayoffHint')}</span>
          </div>

          {!form.isPlayoff ? (
            <div className="st-field st-field-wide">
              <label>{t('trStageGroup')}</label>
              <select
                className="st-select"
                value={form.group_id}
                onChange={(event) => updateField('group_id', event.target.value)}
              >
                <option value="">{t('trStageGroupNone')}</option>
                {seasonGroups.map((group) => (
                  <option key={group.id} value={String(group.id)}>{group.name}</option>
                ))}
              </select>
              <span className="st-photo-hint">{t('trStageGroupHint')}</span>
            </div>
          ) : null}

          <div className="st-field">
            <label>{t('trStageRateRatio')}</label>
            <select
              className="st-select"
              value={form.rate_ratio}
              onChange={(event) => updateField('rate_ratio', event.target.value)}
            >
              <option value="">{t('trRateRatioPlaceholder')}</option>
              {Array.from(new Set([
                ...RATE_RATIOS,
                form.rate_ratio === '' ? null : Number(form.rate_ratio),
              ].filter((value) => value != null && !Number.isNaN(value)))).sort((a, b) => a - b).map((value) => (
                <option key={value} value={String(value)}>{value}</option>
              ))}
            </select>
            <span className="st-photo-hint">{t('trStageRateRatioHint')}</span>
          </div>
        </>
      ) : null}
    </div>
  );

  const matchesBlock = (
    <>
      {matchesLoading ? (
        <div className="st-loading">
          <Loader type="Oval" color="#3ad1a0" height={36} width={36} />
        </div>
      ) : matches.length > 0 ? (
        <div className="stage-matches">
          {matches.map((match) => {
            const when = formatMatchWhen(match);
            return (
              <Link
                key={match.id ?? match.match_id}
                to={`/manage_match/${match.id ?? match.match_id}`}
                className="match-row"
              >
                <div className="match-date">{when || '—'}</div>
                <div className="match-main">
                  <div className="match-teams">
                    <span className="team-name">{match.home_team_name || '—'}</span>
                    <span className="match-score">
                      {match.status > 0 ? `${match.home_team_scores || 0}:${match.guest_team_scores || 0}` : 'vs'}
                    </span>
                    <span className="team-name">{match.guest_team_name || '—'}</span>
                  </div>
                  {when ? <small className="match-meta">{when}</small> : null}
                </div>
                {canWrite ? (
                <button
                  type="button"
                  className="btn-icon btn-danger match-row-delete"
                  title={t('delete')}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setMatchToDelete(match);
                  }}
                >
                  <FiTrash2 />
                </button>
                ) : null}
              </Link>
            );
          })}
        </div>
      ) : (
        <div className="st-empty">
          <p>{t('stNoMatches')}</p>
        </div>
      )}
    </>
  );

  const deleteModal = (
    <CustomModal
      styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
      visible={deleteVisible}
      closeModal={() => setDeleteVisible(false)}
    >
      <div className="st-delete-modal">
        <div className="customModalHeader">{t('trDeleteStageTitle')}</div>
        <p>{t('trDeleteStageText')}</p>
        <div className="st-delete-actions">
          <button type="button" className="btn-modal btn-secondary-modal" onClick={() => setDeleteVisible(false)}>
            {t('cancel')}
          </button>
          <button type="button" className="btn-modal btn-danger-modal" disabled={deleting} onClick={handleDelete}>
            {t('delete')}
          </button>
        </div>
      </div>
    </CustomModal>
  );

  const deleteMatchModal = (
    <CustomModal
      styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
      visible={Boolean(matchToDelete)}
      closeModal={() => !deletingMatch && setMatchToDelete(null)}
    >
      <div className="st-delete-modal">
        <div className="customModalHeader">{t('trDeleteMatchTitle')}</div>
        <p>
          {matchToDelete
            ? `${matchToDelete.home_team_name || '—'} — ${matchToDelete.guest_team_name || '—'}. ${t('trDeleteMatchText')}`
            : t('trDeleteMatchText')}
        </p>
        <div className="st-delete-actions">
          <button
            type="button"
            className="btn-modal btn-secondary-modal"
            disabled={deletingMatch}
            onClick={() => setMatchToDelete(null)}
          >
            {t('cancel')}
          </button>
          <button
            type="button"
            className="btn-modal btn-danger-modal"
            disabled={deletingMatch}
            onClick={handleDeleteMatch}
          >
            {t('delete')}
          </button>
        </div>
      </div>
    </CustomModal>
  );

  const matchModals = !isNew ? (
    <>
      <CreateMatchModal
        isOpen={createMatchOpen}
        onClose={() => setCreateMatchOpen(false)}
        onSuccess={handleMatchesChanged}
        preselectedCountryId={Number(countryId) || undefined}
        preselectedCountryName={countryName || undefined}
        preselectedLeague={leagueRef}
        preselectedSeason={seasonRef}
        preselectedStage={stageForModals}
      />
      <GenerateTourModal
        isOpen={generateOpen}
        onClose={() => setGenerateOpen(false)}
        onSuccess={handleMatchesChanged}
        league={leagueRef}
        season={seasonRef}
        stage={stageForModals}
        previousStage={previousStage}
      />
    </>
  ) : null;

  if (isNew) {
    return (
      <div className="layoutWrapper st-layout">
        <div className="matchCenterWrapper st-page">
          <div className="matchCenterHeader">
            <div className="st-header">
              <StadiumCrumbs items={crumbs} />
            </div>
          </div>

          <div className="st-scroll">
            <div className="st-content">
              {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}
              <div className="st-block">
                {loading ? (
                  <div className="st-loading">
                    <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                    <p>{t('trLoading')}</p>
                  </div>
                ) : stageForm}
              </div>
            </div>
          </div>

          <div className="st-actions">
            {canWrite ? (
            <button
              type="button"
              className="st-btn st-btn-primary"
              disabled={loading || saving}
              onClick={handleSave}
            >
              <FiSave /> {saving ? t('trLoading') : t('trCreateStage')}
            </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page dq-page tr-stage-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={crumbs} />
            {leaguesAccess.canRead ? (
              <Link
                to={`/league_tour_team?country=${countryId}&league=${leagueId}&season=${seasonId}&tour=${stageId}`}
                className="st-btn st-btn-secondary"
              >
                <FiUsers />
                <span className="tr-btn-full">{t('navTourTeam')}</span>
                <span className="tr-btn-short">{t('navTourTeamShort')}</span>
              </Link>
            ) : null}
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="st-scroll dq-scroll">
          <div className="dq-split">
            <section className="st-block dq-panel">
              <h2 className="st-block-header">{t('trStageMatches')}</h2>
              <p className="st-hint">{t('trStageMatchesHint')}</p>
              <div className="dq-panel-body">
                {loading ? (
                  <div className="st-loading">
                    <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                    <p>{t('trLoading')}</p>
                  </div>
                ) : matchesBlock}
              </div>
              <div className="st-actions">
                {canWrite ? (
                <>
                <button
                  type="button"
                  className="st-btn st-btn-secondary"
                  disabled={loading}
                  onClick={() => setGenerateOpen(true)}
                >
                  <FiPlus />
                  <span className="tr-btn-full">{t('stGenerate')}</span>
                  <span className="tr-btn-short">{t('stGenerateShort')}</span>
                </button>
                <button
                  type="button"
                  className="st-btn st-btn-primary"
                  disabled={loading}
                  onClick={() => setCreateMatchOpen(true)}
                >
                  <FiPlus />
                  <span className="tr-btn-full">{t('stCreateMatch')}</span>
                  <span className="tr-btn-short">{t('stCreateMatchShort')}</span>
                </button>
                </>
                ) : null}
              </div>
            </section>

            <section className="st-block dq-panel">
              <h2 className="st-block-header">{t('trEditStage')}</h2>
              <div className="dq-panel-body">
                {loading ? (
                  <div className="st-loading">
                    <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                    <p>{t('trLoading')}</p>
                  </div>
                ) : stageForm}
              </div>
              <div className="st-actions">
                {canWrite ? (
                <>
                <button
                  type="button"
                  className="st-btn st-btn-danger"
                  disabled={loading || saving || deleting}
                  onClick={() => setDeleteVisible(true)}
                >
                  {t('delete')}
                </button>
                <button
                  type="button"
                  className="st-btn st-btn-primary"
                  disabled={loading || saving}
                  onClick={handleSave}
                >
                  <FiSave />
                  <span className="tr-btn-full">{saving ? t('trLoading') : t('stdSaveChanges')}</span>
                  <span className="tr-btn-short">{saving ? t('trLoading') : t('stdSaveChangesShort')}</span>
                </button>
                </>
                ) : null}
              </div>
            </section>
          </div>
        </div>
      </div>

      {deleteModal}
      {deleteMatchModal}
      {matchModals}
    </div>
  );
}
